/// <amd-module name="Repos/PullRequest/Attachments/Attachment" />
import * as React from "react";
import { IPullRequestMarkdownToolbarContext } from "Repos/Common/Contracts/Markdown";
import { IPullRequestAttachmentService } from "Repos/PullRequest/Attachments/Service/PullRequestAttachmentService";
import { IMarkdownFormattingArgs, IMarkdownTextSource } from "VSS/Features/MarkdownEditor/Formatting/Contracts";
import { IVssPageContext, IVssPageContextInternal } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { AttachmentsDropTarget } from "./Components/AttachmentsDropTarget";

const attachmentServiceName = "pull-request-attachment-service";

function getAttachmentsService(pageContext: IVssPageContext): IPullRequestAttachmentService | undefined {
    return (pageContext as IVssPageContextInternal).getService<IPullRequestAttachmentService>(attachmentServiceName, false, true);
}

async function addFilesToMarkdown(
    pageContext: IVssPageContext,
    pullRequestContext: IPullRequestMarkdownToolbarContext,
    files: File[],
    formattingArgs: IMarkdownFormattingArgs,
    textSource: IMarkdownTextSource
): Promise<void> {
    // If attachments are not supported, there will be no attachment service
    // There is no need to do anything in this case
    const attachmentsService = getAttachmentsService(pageContext);
    if (attachmentsService) {
        try {
            const formattingResult = await attachmentsService.addAttachmentsToMarkdown(pullRequestContext, files, formattingArgs, (e: Error) =>
                textSource.showError(e)
            );
            // We already ensure textSource above
            textSource.applyFormatting(formattingResult);
        } catch (e) {
            textSource.showError(e);
        }
    }
}

export function calculateTrueContentLength(pageContext: IVssPageContext, content: string) {
    const attachmentsService = getAttachmentsService(pageContext);
    if (attachmentsService) {
        const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
        return attachmentsService.calculateTrueContentLength(content, pageData ? pageData.hostName : "");
    }
    return content.length;
}

export async function tryAttachingPastedImage(
    event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    pageContext: IVssPageContext,
    pullRequestContext: IPullRequestMarkdownToolbarContext,
    textSource: IMarkdownTextSource | null
): Promise<void> {
    let formattingArgs: IMarkdownFormattingArgs | void;
    if (textSource) {
        formattingArgs = textSource.getFormattingArgs();
    }

    if (formattingArgs && event.clipboardData) {
        const { files, items } = event.clipboardData;
        if (files && files.length === 1 && files[0].type.indexOf("image/") >= 0) {
            await addFilesToMarkdown(pageContext, pullRequestContext, [files[0]], formattingArgs, textSource!);
        } else if (items && items.length === 1 && items[0].type.indexOf("image/") >= 0) {
            const itemAsFile = items[0].getAsFile();
            if (itemAsFile) {
                await addFilesToMarkdown(pageContext, pullRequestContext, [itemAsFile], formattingArgs, textSource!);
            }
        }
    }
}

export async function tryAttachingDroppedFiles(
    event: React.DragEvent<HTMLDivElement>,
    pageContext: IVssPageContext,
    pullRequestContext: IPullRequestMarkdownToolbarContext,
    textSource: IMarkdownTextSource | null
): Promise<void> {
    let formattingArgs: IMarkdownFormattingArgs | void;
    if (textSource) {
        formattingArgs = textSource.getFormattingArgs();
    }

    if (formattingArgs && event.dataTransfer) {
        const files: File[] = [];
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
            files.push(event.dataTransfer.files[i]);
        }

        if (files.length > 0) {
            await addFilesToMarkdown(pageContext, pullRequestContext, files, formattingArgs, textSource!);
        }
    }
}

export function tryReplacingAttachments(
    pageContext: IVssPageContext,
    content: string,
    pullRequestContext: IPullRequestMarkdownToolbarContext
): Promise<string> {
    // No attachment involved if there is no service, just return the original description
    const attachmentsService = getAttachmentsService(pageContext);
    if (attachmentsService) {
        return attachmentsService.replaceAttachmentUrls(pullRequestContext, content)
    }
    return Promise.resolve(content);
}

export async function tryUpdateBlobs(
    pageContext: IVssPageContext,
    pullRequestContext: IPullRequestMarkdownToolbarContext,
    content: string
): Promise<string> {
    // If attachments are not supported, there will be no attachment service
    // There is no need to do anything in this case
    const attachmentsService = getAttachmentsService(pageContext);
    if (attachmentsService) {
        return attachmentsService.tryUpdateBlobs(pullRequestContext, content);
    }
    return Promise.resolve(content);
}

export function wrapWithAttachmentDropTarget(
    pageContext: IVssPageContext,
    pullRequestMarkdownContext: IPullRequestMarkdownToolbarContext,
    textField: JSX.Element,
    textSourceRef: React.RefObject<IMarkdownTextSource>
): JSX.Element {
    return (
        <AttachmentsDropTarget onFilesDrop={e => tryAttachingDroppedFiles(e, pageContext, pullRequestMarkdownContext, textSourceRef.current)}>
            {textField}
        </AttachmentsDropTarget>
    );
}
